import React, { useState } from 'react';
import '../Css/Driverpdf.css';
import '../Css/ReportId.css';
import { MdDownloadForOffline } from "react-icons/md";
import Navbar from './Navbar';
import { Typography, CircularProgress, TextField, Button } from '@mui/material';
import Logger from './Logger';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PDFDocument from './PDFDocument';
import { FaSearch } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const ReportId = () => {
  const adminEmail = sessionStorage.getItem('adminEmail');
  const [data, setData] = useState([]);
  const [order, setOrder] = useState([]);
  const [failedOrders, setFailedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportId, setReportId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [activeOrders, setActiveOrders] = useState([]);
  const [deliver, setDeliver] = useState({ assignedCount: 0, completedCount: 0, incompleteCount: 0 });
  const [working, setWorking] = useState();

  const logger = new Logger(getLoggerBaseURL());

  // Function to parse the Report ID into date and serial number
  const parseReportId = (reportId) => {
    if (reportId.length < 7 || reportId.length > 8) {
      return { formattedDate: '', serialNumber: '' };
    }
    const datePart = reportId.slice(0, 6); // First 6 characters for the date
    const serialNumber = reportId.slice(6); // Remaining characters for the serial number

    const day = datePart.slice(0, 2);
    const month = datePart.slice(2, 4);
    const year = '20' + datePart.slice(4, 6); // Assuming year starts with '20'

    const formattedDate = `${year}-${month}-${day}`; // Format: 'yyyy-mm-dd'
    return { formattedDate, serialNumber };
  };

  // Function to handle the search action
  const handleSearch = async () => {
    setReportId(inputValue);
    if (!inputValue) {
      setError('Please enter a Report ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { formattedDate, serialNumber } = parseReportId(inputValue);

      if (!formattedDate || !serialNumber) {
        setError('Invalid Report ID format');
        setLoading(false);
        setFailedOrders([]);
        return;
      }

      // Fetch driver data based on the serial number
      const response = await fetch(`/api/driver/serial/${serialNumber}/${adminEmail}`);
      if (response.ok) {
        const data = await response.json();
        setData(data);

        if (!data) {
          setError('No driver data found for the provided Report ID');
          setLoading(false);
          return;
        }
        const driverEmail = data.email;

        // Fetch order data
        const [orderResponse, deliverResponse, failedOrdersResponse, workingResponse, activeOrdersResponse] = await Promise.all([
          fetch(`/api/driver/report/${driverEmail}/${formattedDate}/${adminEmail}`),
          fetch(`/api/driver/deliver/${driverEmail}/${formattedDate}/${adminEmail}`),
          fetch(`/api/ordera/incompleted/${adminEmail}?time=${formattedDate}&email=${driverEmail}`),
          fetch(`/driver/workingHours/${driverEmail}/${formattedDate}`),
          fetch(`/api/ordera/activeOrders/${adminEmail}?time=${formattedDate}&email=${driverEmail}`)
        ]);

        if (workingResponse.ok) {
          const work = await workingResponse.json();
          setWorking(work);
        }

        if (orderResponse.ok) {
          const orderData = await orderResponse.json();
          setOrder(orderData);

        } else {
          setOrder([]);
          logger.log('Failed to fetch order data', 'ERROR');
          setError('Failed to fetch order data');
        }

        if (deliverResponse.ok) {
          const deliverData = await deliverResponse.json();
          setDeliver(deliverData);

        } else {
          setDeliver([]);
          logger.log('Failed to fetch delivery data', 'ERROR');
          setError('Failed to fetch delivery data');
        }

        if (failedOrdersResponse.ok) {
          const failedData = await failedOrdersResponse.json();
          setFailedOrders(failedData);
        } else {
          logger.log('Failed to fetch failed orders', 'ERROR');
          setFailedOrders([]);
          setError('No report found for this Report ID');
        }

        if (activeOrdersResponse.ok) {
          const activeData = await activeOrdersResponse.json();
          setActiveOrders(activeData); // Store active orders in state
        } else {
          logger.log('Failed to fetch active orders', 'ERROR');
        }

        setLoading(false);
      } else {
        logger.log('Failed to fetch driver data', 'ERROR');
        setError('Failed to fetch driver data');
        setLoading(false);
      }
    } catch (error) {
      logger.log(`Error fetching data: ${error.message}`, 'ERROR');
      setError('Error fetching data');
      setLoading(false);
    }
  };

  // Display a loading spinner while data is being fetched
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography variant="h6" style={{ marginTop: '20px' }}>Fetching Data...</Typography>
      </div>
    );
  }

  const uniquePersonalInfo = data;

  // Function to calculate the total amount for orders
  const calculateTotalAmount = (orders, field) => {
    if (!orders || !orders.length) {
      return 0;
    }
    return orders.reduce((total, item) => total + parseFloat(item[field] || 0), 0);
  };

  const totalOrders = deliver.assignedCount + deliver.completedCount + deliver.incompleteCount;

  // Define mopValues inside the component to ensure it's available during render
  const mopValues = Array.from(new Set(order.flatMap(item => [item.mop, item.mop2, item.mop3].filter(value => value && value !== 'null'))));

  return (
    <div className="reportId-page">
      <Navbar />
      <h2 className="headS">
        <Link to="/Reports" className="reports">Reports</Link>
        <span className="crumbSep">{">"}</span>
        <span className="reporter">Report by ID</span>
      </h2>
      <div className="reportdCompleteS" id="pdf-content">
        {/* Search Box */}
        <div style={{ margin: '20px 0' }}>
          <TextField
            label="Enter Report ID"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            variant="outlined"
            sx={{
              paddingBottom: '10px',
              marginRight: '10px',
              width: '300px',
              '& .MuiInputBase-root': {
                height: '53px',
              },
              '& .MuiFormControl-root': {
                height: '100%',
              }
            }}
          />
          <Button variant="contained" color="primary" onClick={handleSearch} style={{ height: '52px', backgroundColor: '#07a6ef' }}>
            <FaSearch style={{ fontSize: '20px' }} />
          </Button>
          <br />
          <p style={{ color: 'grey', textAlign: 'center', fontSize: '14px', marginLeft: '-10px' }}>
            Note: To get the <b>Report ID</b>, go to <b><Link to="/summary" className="reportsNote">Reports {'>'} Daily Summary</Link></b>
          </p>
        </div>

        {/* Error Message */}
        {error && <div style={{ color: 'red', marginBottom: '20px' }}>Error: {error}</div>}

        {/* Display Report Content if data is available */}
        {data && deliver.completedCount !== 0 ? (
          <>
            {/* PDF Generation Button */}
            {data && (
              <div style={{ margin: '20px 0' }}>
                <PDFDownloadLink
                  document={<PDFDocument data={data} deliver={deliver} failedOrders={failedOrders} reportId={reportId} order={order} working={working} active={activeOrders} />}
                  fileName={`DriverReport_${reportId}.pdf`}
                >
                  {({ loading }) =>
                    loading ? <CircularProgress /> :
                      <button
                        className="generate-pdf-button"
                        style={{ background: 'rgb(121, 198, 233)', border: 'none', cursor: 'pointer', padding: '10px', borderRadius: '5px' }}
                        data-tooltip-id="download-tooltip"><MdDownloadForOffline size={30} /></button>
                  }
                </PDFDownloadLink>
              </div>
            )}
            {/* Report Information */}
            <p><strong>Date:</strong> {parseReportId(reportId).formattedDate}</p>
            <p><strong>Report ID:</strong> {reportId}</p>

            {/* Personal Information and Order Details */}
            <div className="tile-container">
              {/* Personal Information */}
              <div className="tile">
                <div className="personalS"><h4>Personal Information</h4></div>
                <hr />
                <div className="personalInfo">
                  {uniquePersonalInfo && (
                    <>
                      <div><strong>Name:</strong> {uniquePersonalInfo.name}</div>
                      <div><strong>Phone Number:</strong> {uniquePersonalInfo.phone}</div>
                      <div><strong>Email:</strong> {uniquePersonalInfo.email}</div>
                      <div><strong>Vehicle:</strong> {uniquePersonalInfo.vehicle}</div>
                    </>
                  )}
                </div>
              </div>

              {/* Order Details */}
              <div className="tile">
                <div className="personalS"><h4>Order Details</h4></div>
                <hr />
                <div className="personS">
                  <div><strong>Total Orders:</strong> {totalOrders}</div>
                  <div><strong>Active Orders:</strong> {deliver.assignedCount}</div>
                  <div><strong>Completed Orders:</strong> {deliver.completedCount}</div>
                  <div><strong>Incomplete Orders:</strong> {failedOrders.length}</div>
                </div>
              </div>
            </div>

            {/* Payment Mode Details */}
            {mopValues.map((paymentMode, index) => {
              const filteredOrders = order.filter(item => item.mop === paymentMode || item.mop2 === paymentMode || item.mop3 === paymentMode);
              if (filteredOrders.length === 0) return null;

              return (
                <div className="bx3" key={index} style={{ marginTop: '20px' }}>
                  <div className="personS">
                    <h5>{index + 1}. {paymentMode}:</h5>
                    <table>
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.Order_No}</td>
                            <td>{item.mop === paymentMode ? item.amount1 : item.amount2}</td>
                          </tr>
                        ))}
                        <tr>
                          <td><strong>Total:</strong></td>
                          <td>
                            {calculateTotalAmount(filteredOrders.filter(item => item.mop === paymentMode), 'amount1') +
                              calculateTotalAmount(filteredOrders.filter(item => item.mop2 === paymentMode), 'amount2') +
                              calculateTotalAmount(filteredOrders.filter(item => item.mop3 === paymentMode), 'amount3')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {/* Displaying Failed Orders */}
            {failedOrders.length > 0 && (
              <div className="bx4" style={{ marginTop: '20px' }}>
                <div className="personalS"><h4>Failed Delivery Orders</h4></div>
                <hr />
                <div className="personS">
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedOrders.map((order, index) => (
                        <tr key={index}>
                          <td>{order.Order_No}</td>
                          <td>{order.reason_failure}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeOrders.length > 0 && (
              <div className="bx4">
                <div className="personS"><h4>Active Orders</h4></div>
                <hr />
                <div className="bx">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Receiver Name</th>
                        <th>Receiver Address</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeOrders.map((order, index) => (
                        <tr key={index}>
                          <td>{order.Order_No}</td>
                          <td>{order.Rec_Name}</td>
                          <td>{order.Rec_Add}</td>
                          <td>{order.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ReportId;
